import { nanoid } from 'nanoid';
import { getApiBaseUrl } from '../config/apiBase';

const LS_KEY = 'mdv-saved-views';
const LS_SHARED_KEY = 'mdv-saved-views-shared';

export function readSavedViews() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeSavedViews(list) {
  localStorage.setItem(LS_KEY, JSON.stringify(list));
  return list;
}

function sharedViewsKey(projectName) {
  return `${LS_SHARED_KEY}:${String(projectName || 'default')}`;
}

export function readCachedSharedViews(projectName) {
  try {
    const raw = localStorage.getItem(sharedViewsKey(projectName));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function writeCachedSharedViews(projectName, list) {
  try {
    localStorage.setItem(sharedViewsKey(projectName), JSON.stringify(Array.isArray(list) ? list : []));
  } catch {
    // quota / private mode
  }
  return list;
}

export function nextViewOrder(views) {
  return (views || []).reduce((max, view) => {
    const n = Number(view?.order);
    return Number.isFinite(n) ? Math.max(max, n) : max;
  }, 0) + 1;
}

function sortViews(list) {
  return [...(list || [])].sort((a, b) => {
    const ao = Number(a?.order);
    const bo = Number(b?.order);
    const aHas = Number.isFinite(ao);
    const bHas = Number.isFinite(bo);
    if (aHas && bHas && ao !== bo) return ao - bo;
    if (aHas !== bHas) return aHas ? -1 : 1;
    return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' });
  });
}

export function captureView({
  name,
  camera,
  target,
  layerVisibility,
  isolatedKey = null,
  isolatedKeys = null,
  sceneBgColor = null,
  sceneLookId = null,
  showGrid = false,
  section = null,
  order = null,
}) {
  const visible = Object.keys(layerVisibility || {}).filter((k) => layerVisibility[k]?.visible);
  const opacity = {};
  Object.keys(layerVisibility || {}).forEach((key) => {
    const o = layerVisibility[key]?.opacity;
    if (typeof o === 'number' && o !== 1) opacity[key] = o;
  });
  const parsedOrder = Number(order);
  return {
    id: nanoid(8),
    name: name || `View ${new Date().toLocaleString()}`,
    createdAt: new Date().toISOString(),
    camera: camera ? { x: camera.x, y: camera.y, z: camera.z } : null,
    target: target ? { x: target.x, y: target.y, z: target.z } : null,
    visible,
    opacity,
    isolatedKeys: Array.isArray(isolatedKeys)
      ? isolatedKeys
      : isolatedKey
        ? [isolatedKey]
        : [],
    isolatedKey: (Array.isArray(isolatedKeys) ? isolatedKeys[0] : isolatedKey) || null,
    sceneBgColor: sceneBgColor ?? null,
    sceneLookId: sceneLookId || null,
    showGrid: Boolean(showGrid),
    section: section || null,
    order: Number.isFinite(parsedOrder) ? parsedOrder : null,
  };
}

export function addSavedView(view) {
  const next = [view, ...readSavedViews().filter((v) => v.id !== view.id)].slice(0, 40);
  return writeSavedViews(next);
}

export function deleteSavedView(id) {
  return writeSavedViews(readSavedViews().filter((v) => v.id !== id));
}

export function mergeLocalAndSharedViews(local, shared) {
  const byId = new Map();
  (local || []).forEach((v) => {
    if (v?.id) byId.set(v.id, { ...v, shared: false });
  });
  (shared || []).forEach((v) => {
    if (v?.id) byId.set(v.id, { ...v, shared: true });
  });
  return sortViews(Array.from(byId.values()));
}

function viewsUrl(projectName, extra = {}) {
  const params = new URLSearchParams({
    project: String(projectName || 'default'),
    ...extra,
  });
  return `${getApiBaseUrl()}/saved_views.php?${params.toString()}`;
}

export async function fetchViewById(projectName, id) {
  const viewId = String(id || '').trim();
  if (!viewId) return null;
  const local = readSavedViews().find((v) => v?.id === viewId);
  if (local) return { ...local, shared: false };
  const cached = readCachedSharedViews(projectName).find((v) => v?.id === viewId);
  if (cached) return { ...cached, shared: true };
  try {
    const res = await fetch(viewsUrl(projectName, { id: viewId }), { credentials: 'include' });
    const data = await res.json();
    if (data?.view?.id) return { ...data.view, shared: true };
    const list = Array.isArray(data?.views) ? data.views : [];
    const found = list.find((v) => v?.id === viewId);
    return found ? { ...found, shared: true } : null;
  } catch {
    return null;
  }
}

export async function fetchSharedViews(projectName) {
  try {
    const res = await fetch(viewsUrl(projectName), { credentials: 'include' });
    const data = await res.json();
    if (!data?.success || !Array.isArray(data.views)) {
      return readCachedSharedViews(projectName);
    }
    writeCachedSharedViews(projectName, data.views);
    return data.views;
  } catch {
    return readCachedSharedViews(projectName);
  }
}

export async function postSharedView(projectName, view) {
  const res = await fetch(viewsUrl(projectName), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(view),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Could not share view');
  }
  const saved = data.view || view;
  const cached = readCachedSharedViews(projectName).filter((v) => v?.id !== saved.id);
  writeCachedSharedViews(projectName, [{ ...saved, shared: true }, ...cached]);
  return saved;
}

export async function deleteSharedViewRemote(projectName, id) {
  const res = await fetch(viewsUrl(projectName, { id: String(id) }), {
    method: 'DELETE',
    credentials: 'include',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || 'Could not delete shared view');
  }
  writeCachedSharedViews(
    projectName,
    readCachedSharedViews(projectName).filter((v) => v?.id !== id)
  );
}
